import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Bell, Search, UserCircle } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";
import { useEffect, useRef, useState } from "react";

const Layout = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const syncRef = useRef(false);
  const userInputRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("q") || "";
    if (location.pathname === "/" && query !== searchValue && !userInputRef.current) {
      syncRef.current = true;
      setSearchValue(query);
    }
  }, [location.pathname, location.search, searchValue]);

  useEffect(() => {
    if (syncRef.current) {
      syncRef.current = false;
      return;
    }
    if (!userInputRef.current) return;
    const trimmed = searchValue.trim();
    const timeout = setTimeout(() => {
      if (!trimmed) {
        navigate("/");
        userInputRef.current = false;
        return;
      }
      if (trimmed.startsWith("@")) {
        const handle = trimmed.slice(1);
        if (handle) {
          navigate(`/user/${handle}`);
        }
        userInputRef.current = false;
        return;
      }
      navigate(`/?q=${encodeURIComponent(trimmed)}`);
      userInputRef.current = false;
    }, 400);
    return () => clearTimeout(timeout);
  }, [navigate, searchValue]);

  const handleProtectedNav = (event, path) => {
    if (!user) {
      event.preventDefault();
      navigate("/auth");
      return;
    }
    navigate(path);
  };

  const goTo = (path) => {
    if (!user) {
      navigate("/auth");
    } else {
      navigate(path);
    }
  };

  return (
    <div className="min-h-screen" data-testid="layout-shell">
      <header className="sticky top-0 z-40 border-b border-border/30 bg-primary shadow-sm">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="text-lg font-semibold tracking-tight text-foreground" data-testid="brand-title">
              Yopinion
            </div>
          </div>
          <div className="flex flex-1 items-center justify-end gap-4 flex-wrap">
            <nav className="flex items-center gap-2" data-testid="top-nav">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-full transition ${
                  isActive
                    ? "bg-foreground text-background"
                    : "text-foreground/70 hover:text-foreground"
                }`
              }
              data-testid="nav-home-tab"
            >
              Home
            </NavLink>
            <NavLink
              to="/my-polls"
              onClick={(event) => handleProtectedNav(event, "/my-polls")}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-full transition ${
                  isActive
                    ? "bg-foreground text-background"
                    : "text-foreground/70 hover:text-foreground"
                }`
              }
              data-testid="nav-my-polls-tab"
            >
              My Polls
            </NavLink>
            <NavLink
              to="/trending"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-full transition ${
                  isActive
                    ? "bg-foreground text-background"
                    : "text-foreground/70 hover:text-foreground"
                }`
              }
              data-testid="nav-trending-tab"
            >
              Trending
            </NavLink>
            </nav>
            <div className="flex items-center gap-2">
            <div className="relative" data-testid="global-search-wrapper">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchValue}
                onChange={(event) => {
                  userInputRef.current = true;
                  setSearchValue(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    const trimmed = searchValue.trim();
                    userInputRef.current = false;
                    if (!trimmed) {
                      navigate("/");
                      return;
                    }
                    if (trimmed.startsWith("@")) {
                      const handle = trimmed.replace("@", "");
                      if (handle) {
                        navigate(`/user/${handle}`);
                      }
                      return;
                    }
                    navigate(`/?q=${encodeURIComponent(trimmed)}`);
                  }
                }}
                placeholder="Search polls or @users"
                className="h-9 w-52 pl-9 text-sm border border-[#121417]"
                data-testid="global-search-input"
              />
            </div>
            {user ? (
              <>
                <button
                  className="h-10 w-10 rounded-full border border-[#121417] flex items-center justify-center hover:bg-accent"
                  onClick={() => goTo("/notifications")}
                  data-testid="nav-notifications-button"
                >
                  <Bell className="h-4 w-4" />
                </button>
                <button
                  className="h-10 w-10 rounded-full border border-border/80 flex items-center justify-center overflow-hidden"
                  onClick={() => goTo("/profile")}
                  data-testid="nav-profile-button"
                >
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.username}
                      className="h-full w-full object-cover"
                      data-testid="nav-profile-avatar"
                    />
                  ) : (
                    <UserCircle className="h-4 w-4" />
                  )}
                </button>
              </>
            ) : (
              <Button
                onClick={() => navigate("/auth")}
                data-testid="nav-login-button"
              >
                Log in
              </Button>
            )}
            </div>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl px-4 pb-20 pt-6" data-testid="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;
